import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import * as am5map from "@amcharts/amcharts5/map";
import * as am5 from "@amcharts/amcharts5";
import am5geodata_worldLow from "@amcharts/amcharts5-geodata/worldLow";
import { ApiService } from "../../Shared/api.service";
import { ToggleDarkModeService } from "../../Shared/toggle-dark-mode.service";
import { DarkTheme } from "@amcharts/amcharts5/.internal/themes/DarkTheme";

const NUM_THREATS = 50;
interface Location {
    id: string,
    title: string,
    city_name: string,
    geometry: {
        type: string,
        coordinates: string[]
    },
    bulletSettings: {
        fill: any
    }
}

interface City { id: string; name: string; };

interface ChartSeries {
    citySeries: any;
    lineSeries: any;
    animatedBulletSeries: any;
    polygonSeries: any;
    animatedLineSeries: any;
}

// noinspection TypeScriptValidateTypes
@Component({
    selector: 'app-map-with-animation',
    templateUrl: './map-with-animation.component.html',
    styleUrls: ['./map-with-animation.component.css'],
    standalone: false,
})
export class MapWithAnimationComponent implements OnInit {
    @Input() threat_map_list: any;
    isDarkTheme: boolean = false;
    response_data: any = [];
    root: any;
    intervalID: any;

    // Container and separate charts
    chartContainer: any;
    mercatorChart: any;
    globeChart: any;

    // Series for mercator chart
    mercatorSeries: ChartSeries = {
        citySeries: null,
        lineSeries: null,
        animatedBulletSeries: null,
        polygonSeries: null,
        animatedLineSeries: null
    };

    // Series for globe chart
    globeSeries: ChartSeries = {
        citySeries: null,
        lineSeries: null,
        animatedBulletSeries: null,
        polygonSeries: null,
        animatedLineSeries: null
    };

    // timeLabel: any;
    currentChart: 'mercator' | 'globe' = 'mercator';

    cities = [
        {
            id: "london",
            title: "London",
            geometry: { type: "Point", coordinates: [-0.1262, 51.5002] },
            bulletSettings: {
                fill: am5.color(0x8edd69)
            }
        },
        {
            id: "kazakhstan",
            title: "Kazakhstan",
            geometry: { type: "Point", coordinates: [48.019573, 66.923683] },
            bulletSettings: {
                fill: am5.color(0xff0000)
            }
        }];

    location_list: any[] = [];
    location_pairs: any[] = [];
    location_time: any[] = [];
    counter = 0

    constructor(private api: ApiService, private helper: ToggleDarkModeService) {
    }

    ngOnInit() {
        this.root = am5.Root.new("chartmapwithanimation");
        this.helper.selected_mode$.subscribe(mode => {
            this.isDarkTheme = mode;
            this.change_theme(this.root);
        })
        this.loadData();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['threat_map_list'] && this.threat_map_list && this.threat_map_list.length > 0) {
            // Only update animation if charts are initialized
            // Interval that cycles through the list of different location pairs for threat map
            this.updateCurrentAnimation();
            setTimeout(() => {
                this.mercatorSeries.lineSeries.data.clear();
                this.mercatorSeries.animatedLineSeries.data.clear();
                this.mercatorSeries.animatedBulletSeries.data.clear();

                this.globeSeries.lineSeries.data.clear();
                this.globeSeries.animatedLineSeries.data.clear();
                this.globeSeries.animatedBulletSeries.data.clear();
            }, 5000);

            // clear the previous interval if it exists
            if (this.intervalID) {
                clearInterval(this.intervalID);
            }

            this.intervalID = setInterval(() => {
                this.mercatorSeries.lineSeries.data.clear();
                this.mercatorSeries.animatedLineSeries.data.clear();
                this.mercatorSeries.animatedBulletSeries.data.clear();

                this.globeSeries.lineSeries.data.clear();
                this.globeSeries.animatedLineSeries.data.clear();
                this.globeSeries.animatedBulletSeries.data.clear();

                setTimeout(() => {
                    this.updateCurrentAnimation();
                }, 500);

                // Check to see if we are at end of data array
                if (this.counter == this.threat_map_list.length - 1) {
                    // Reset counter
                    this.counter = 0;
                } else {
                    this.counter = this.counter + 1;
                }
            }, 5500);
        }
    }

    private loadData() {
        const that = this;
        this.root.setThemes([
            am5themes_Animated.new(this.root)
        ]);
        let use_fake_data = false;
        console.log("Loading threat map data...");
        // Check if we have valid threat_map_list data, otherwise use demo data
        if (!this.threat_map_list || this.threat_map_list.length === 0) {
            use_fake_data = true;
            // Load dummy data, something was wrong with the API or no data provided
            this.threat_map_list = [
                {
                    dest_city: {
                        id: "paris",
                        title: "Paris",
                        city_name: "Paris",
                        geometry: { coordinates: [2.3510, 48.8567] }
                    },
                    source_city: {
                        id: "moscow",
                        title: "Moscow",
                        citu_name: "Moscow",
                        geometry: { coordinates: [37.6176, 55.7558] }
                    },
                    time_stamp: "2024-01-19 23:08:02"
                },
                {
                    dest_city: {
                        id: "paris",
                        title: "Paris",
                        city_name: "Paris",
                        geometry: { coordinates: [2.3510, 48.8567] }
                    },
                    source_city: {
                        id: "new york",
                        title: "New York",
                        city_name: "New York",
                        geometry: { coordinates: [-74, 40.43] }
                    },
                    time_stamp: "2024-01-20 20:58:57"
                },
                {
                    dest_city: {
                        id: "new york",
                        title: "New York",
                        city_name: "New York",
                        geometry: { coordinates: [-74, 40.43] }
                    },
                    source_city: {
                        id: "another_moscow",
                        title: "Moscow",
                        city_name: "Moscow",
                        geometry: { coordinates: [37.6176, 55.7558] }
                    },
                    time_stamp: "2024-01-21 04:28:32"
                }
            ];
        }

        // Create main container
        this.chartContainer = this.root.container.children.push(am5.Container.new(this.root, {
            width: am5.percent(100),
            height: am5.percent(100)
        }));

        // Create both charts
        this.createMercatorChart();
        this.createGlobeChart();

        // Create shared UI elements
        this.createSharedUI();

        // Initialize with data
        if (!use_fake_data) {
            this.updateCurrentAnimation();
        }
    }

    private createMercatorChart() {
        // Create the mercator map chart
        this.mercatorChart = this.chartContainer.children.push(am5map.MapChart.new(this.root, {
            panX: "translateX",
            panY: "translateY",
            projection: am5map.geoMercator(),
            width: am5.percent(100),
            height: am5.percent(100)
        }));

        // Create series for mercator chart
        this.mercatorSeries = this.createSeriesForChart(this.mercatorChart, false);

    }

    private createGlobeChart() {
        // Create the globe map chart (initially hidden)
        this.globeChart = this.chartContainer.children.push(am5map.MapChart.new(this.root, {
            panX: "rotateX",
            panY: "rotateY",
            projection: am5map.geoOrthographic(),
            width: am5.percent(100),
            height: am5.percent(100),
            visible: false
        }));

        // Create series for globe chart
        this.globeSeries = this.createSeriesForChart(this.globeChart, true);
    }

    private createSeriesForChart(chart: any, isGlobe: boolean): ChartSeries {
        let exclusions = !isGlobe ? ["AQ"] : [];
        const series: ChartSeries = {
            citySeries: null,
            lineSeries: null,
            animatedBulletSeries: null,
            polygonSeries: null,
            animatedLineSeries: null
        };

        // Add a background polygon covering the whole world
        let backgroundSeries = chart.series.unshift(
            am5map.MapPolygonSeries.new(this.root, {})
        );
        backgroundSeries.mapPolygons.template.setAll({
            fill: am5.color(0xd5deec), // ocean color
            stroke: am5.color(0xd5deec),
            fillOpacity: 1,
            strokeOpacity: 1
        });
        backgroundSeries.data.push({
            geometry: am5map.getGeoRectangle(90, 180, -90, -180)
        });

        // Create main polygon series for countries
        series.polygonSeries = chart.series.push(am5map.MapPolygonSeries.new(this.root, {
            exclude: exclusions,
            geoJSON: am5geodata_worldLow
        }));

        // let graticuleSeries = chart.series.push(am5map.GraticuleSeries.new(this.root, {}));
        // graticuleSeries.mapLines.template.setAll({
        //     stroke: this.root.interfaceColors.get("alternativeBackground"),
        //     strokeOpacity: 0.08
        // });

        // Invisible line series for animation paths
        series.lineSeries = chart.series.push(am5map.MapLineSeries.new(this.root, {}));
        series.lineSeries.mapLines.template.setAll({
            stroke: this.root.interfaceColors.get("alternativeBackground"),
            strokeOpacity: 0,
        });

        // Visible animated line series
        series.animatedLineSeries = chart.series.push(am5map.MapLineSeries.new(this.root, {}));
        series.animatedLineSeries.mapLines.template.setAll({
            stroke: am5.color(0x000000),
            strokeOpacity: 0.6,
            strokeDasharray: 0,
            strokeWidth: 2,
        });

        // City point series
        series.citySeries = chart.series.push(
            am5map.MapPointSeries.new(this.root, {})
        );

        // Visible city circles
        const that = this;
        series.citySeries.bullets.push(function () {
            let circle = am5.Circle.new(that.root, {
                radius: 4, // smaller radius
                tooltipText: "{title}",
                tooltipY: 0,
                templateField: "bulletSettings",
                stroke: am5.color(0x000000), // black stroke
                strokeWidth: 2,
                fill: am5.color(0xd40202) // red color
            });

            circle.animate({
                key: "radius",
                duration: 2000,
                to: 5, // animate to a slightly larger radius, still small
                loops: Infinity,
                easing: am5.ease.yoyo(am5.ease.linear)
            })

            return am5.Bullet.new(that.root, {
                sprite: circle
            });
        });

        // Invisible animated bullet series
        series.animatedBulletSeries = chart.series.push(
            am5map.MapPointSeries.new(this.root, {})
        );

        series.animatedBulletSeries.bullets.push(function () {
            let circle = am5.Circle.new(that.root, {
                radius: 0
            });

            return am5.Bullet.new(that.root, {
                sprite: circle
            });
        });

        return series;
    }

    private createSharedUI() {
        let cont = this.chartContainer.children.push(am5.Container.new(this.root, {
            layout: this.root.horizontalLayout,
            x: 20,
            y: 40
        }));

        cont.children.push(am5.Label.new(this.root, {
            centerY: am5.p50,
            text: "Map",
            fill: this.isDarkTheme ? am5.color(0xffffff) : am5.color(0x000000)
        }));
        let switchButton = cont.children.push(am5.Button.new(this.root, {
            themeTags: ["switch"],
            centerY: am5.p50,
            icon: am5.Circle.new(this.root, {
                themeTags: ["icon"]
            })
        }));

        const that = this;
        switchButton.on("active", function () {
            if (!switchButton.get("active")) {
                // Switch to mercator
                that.mercatorChart.set("visible", true);
                that.globeChart.set("visible", false);
                that.currentChart = 'mercator';
            } else {
                // Switch to globe
                that.mercatorChart.set("visible", false);
                that.globeChart.set("visible", true);
                that.currentChart = 'globe';
            }
        });

        cont.children.push(am5.Label.new(this.root, {
            centerY: am5.p50,
            text: "Globe",
            fontSize: 16,
            fill: this.isDarkTheme ? am5.color(0xffffff) : am5.color(0x000000)
        }));
    }

    private updateCurrentAnimation() {
        // Prepare three threats for animation
        this.format_locations(); // Now fills arrays with three threats

        // Deduplicate city data from the three threat pairs
        const uniqueCitiesMap = new Map<string, any>();
        this.location_list.slice(0, NUM_THREATS).forEach(pair => {
            pair.forEach((city: City) => {
                // If the city already exists, skip; otherwise add it.
                if (!uniqueCitiesMap.has(city?.id)) {
                    uniqueCitiesMap.set(city?.id, city);
                }
            });
        });
        const combinedCityData = Array.from(uniqueCitiesMap.values());

        // Set unique cities into both charts
        this.mercatorSeries.citySeries.data.setAll(combinedCityData);
        this.globeSeries.citySeries.data.setAll(combinedCityData);

        // Start animations for each threat concurrently
        for (let i = 0; i < NUM_THREATS; i++) {
            this.load_animation(this.location_pairs[i], this.mercatorSeries);
            this.load_animation(this.location_pairs[i], this.globeSeries);
        }
    }

    private format_locations() {
        const data = this.threat_map_list;

        // Safety check to ensure we have valid data
        if (!data || data.length === 0) {
            console.warn('No valid threat data, using demo data.');
            // Optionally reset or load demo data...
            return;
        }

        // For running three animations concurrently:
        this.location_list = [];
        this.location_pairs = [];
        this.location_time = [];

        for (let i = 0; i < NUM_THREATS; i++) {
            // Calculate the index relative to counter (wrap if needed)
            let index = (this.counter + i) % data.length;
            let temp_data = data[index];
            // console.log("Threat data:", temp_data);

            let dest_data_structure: Location = {
                id: temp_data.dest_city.id,
                title: temp_data.dest_city.city_name + ", " + temp_data.dest_city.title, // Use city_name for label
                city_name: temp_data.dest_city.city_name,
                geometry: {
                    type: "Point",
                    coordinates: [
                        temp_data.dest_city.geometry.coordinates[0],
                        temp_data.dest_city.geometry.coordinates[1]
                    ]
                },
                bulletSettings: {
                    fill: am5.color(0x8edd69)
                }
            };

            let src_data_structure: Location = {
                id: temp_data.source_city.id,
                title: temp_data.source_city.city_name + ", " + temp_data.source_city.title, // Use city_name for label
                city_name: temp_data.source_city.city_name,
                geometry: {
                    type: "Point",
                    coordinates: [
                        temp_data.source_city.geometry.coordinates[0],
                        temp_data.source_city.geometry.coordinates[1]
                    ]
                },
                bulletSettings: {
                    fill: am5.color(0xff0000)
                }
            };

            this.location_list.push([dest_data_structure, src_data_structure]);
            this.location_pairs.push([dest_data_structure.id, src_data_structure.id]);
            this.location_time.push(temp_data.time_stamp);
        }

        // Update counter for next cycle
        this.counter = (this.counter + NUM_THREATS) % data.length;
    }

    private static readonly SEGMENT_LENGTH = 0.2; // 20% of the line

    private load_animation(city: any, series: ChartSeries) {
        let destinationDataItem: any = series.citySeries.getDataItemById(city[0]);
        let sourceDataItem: any = series.citySeries.getDataItemById(city[1]);

        let lineDataItem = series.lineSeries.pushDataItem({});
        lineDataItem.set("pointsToConnect", [sourceDataItem, destinationDataItem])

        // Set initial positions for a fixed-length segment
        let startDataItem: any = series.animatedBulletSeries.pushDataItem({});
        let endDataItem: any = series.animatedBulletSeries.pushDataItem({});

        startDataItem.setAll({
            lineDataItem: lineDataItem,
            positionOnLine: 0
        });
        endDataItem.setAll({
            lineDataItem: lineDataItem,
            positionOnLine: MapWithAnimationComponent.SEGMENT_LENGTH
        });

        let animatedLineDataItem = series.animatedLineSeries.pushDataItem({});
        animatedLineDataItem.set("pointsToConnect", [startDataItem, endDataItem])

        let lon0: any = sourceDataItem.get("longitude");
        let lat0: any = sourceDataItem.get("latitude");
        let lon1: any = destinationDataItem.get("longitude");
        let lat1: any = destinationDataItem.get("latitude");

        let distance = Math.hypot(lon1 - lon0, lat1 - lat0);
        let duration = 5000;

        this.animateSegment(startDataItem, endDataItem, duration);
    }

    private animateSegment(startDataItem: any, endDataItem: any, duration: any) {
        const segLen = MapWithAnimationComponent.SEGMENT_LENGTH;
        const travelDuration = duration * 0.7; // 70% of time for travel
        const shrinkDuration = duration * 0.3; // 30% for shrinking

        // Phase 1: Travel with fixed length
        let _startAnimation = startDataItem.animate({
            key: "positionOnLine",
            from: 0,
            to: 1 - segLen,
            duration: travelDuration
        });
        let endAnimation = endDataItem.animate({
            key: "positionOnLine",
            from: segLen,
            to: 1,
            duration: travelDuration
        });

        const that = this;

        // When travel phase ends, start shrink phase
        endAnimation.events.on("stopped", function () {
            // Phase 2: Shrink segment at destination
            let shrinkStart = startDataItem.animate({
                key: "positionOnLine",
                from: 1 - segLen,
                to: 1,
                duration: shrinkDuration
            });
            let shrinkEnd = endDataItem.animate({
                key: "positionOnLine",
                from: 1,
                to: 1,
                duration: shrinkDuration
            });

            shrinkEnd.events.on("stopped", function () {
                // Reset to start and repeat
                startDataItem.set("positionOnLine", 0);
                endDataItem.set("positionOnLine", segLen);
                that.animateSegment(startDataItem, endDataItem, duration);
            });
        });
    }

    private animateStart(startDataItem: any, endDataItem: any, duration: any) {
        let startAnimation = startDataItem.animate({
            key: "positionOnLine",
            from: 0,
            to: 1,
            duration: duration
        });

        const that = this;
        startAnimation.events.on("stopped", function () {
            that.animateEnd(startDataItem, endDataItem, duration);
        });
    }

    private animateEnd(startDataItem: any, endDataItem: any, duration: any) {
        startDataItem.set("positionOnLine", 0)
        let endAnimation = endDataItem.animate({
            key: "positionOnLine",
            from: 0,
            to: 1,
            duration: duration
        })

        const that = this;
        endAnimation.events.on("stopped", function () {
            that.animateStart(startDataItem, endDataItem, duration);
        });
    }

    private change_theme(root: any) {
        if (this.isDarkTheme) {
            root.setThemes([
                DarkTheme.new(root)
            ]);
        } else {
            root.setThemes([
                am5themes_Animated.new(root)
            ]);
        }
        
        // Update label colors based on theme
        this.updateLabelColors();
    }

    private updateLabelColors() {
        // Find and update the label colors in the chart container
        if (this.chartContainer) {
            this.chartContainer.children.each((child: any) => {
                if (child instanceof am5.Container) {
                    child.children.each((grandChild: any) => {
                        if (grandChild instanceof am5.Label) {
                            grandChild.set("fill", this.isDarkTheme ? am5.color(0xffffff) : am5.color(0x000000));
                        }
                    });
                }
            });
        }
    }
}
